import { useEffect, useMemo, useState } from 'react';
import {
  chartPriceDomain,
  filterChartPoints,
  formatUsd,
  type ChartRange,
} from '../lib/screener.js';

type Point = { observedAt: string; price: number };
type PositionedPoint = Point & { time: number; x: number; y: number };

function pointTime(point: Point): number {
  return new Date(point.observedAt.length === 10 ? `${point.observedAt}T00:00:00Z` : point.observedAt).valueOf();
}

export function HistoryChart({ points }: { points: Point[] }) {
  const [range, setRange] = useState<ChartRange>('1M');
  useEffect(() => setRange('1M'), [points]);

  const filtered = useMemo(() => filterChartPoints(points, range), [points, range]);

  const geometry = useMemo(() => {
    if (filtered.length < 2) return null;
    const times = filtered.map(pointTime);
    const prices = filtered.map(point => point.price);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const domain = chartPriceDomain(prices);
    const positioned: PositionedPoint[] = filtered.map((point, index) => ({
      ...point,
      time: times[index],
      x: 62 + ((times[index] - minTime) / (maxTime - minTime || 1)) * 518,
      y: 20 + (1 - (point.price - domain.min) / (domain.max - domain.min)) * 168,
    }));
    return {
      positioned,
      path: positioned.map((point, index) => `${index ? 'L' : 'M'} ${point.x} ${point.y}`).join(' '),
      minPrice: domain.min,
      maxPrice: domain.max,
      minTime,
      maxTime,
    };
  }, [filtered]);

  const sparse = useMemo(() => filtered.some((point, index) => (
    index > 0 && pointTime(point) - pointTime(filtered[index - 1]) > 2 * 86_400_000
  )), [filtered]);

  return (
    <section className="chart" aria-labelledby="history-heading">
      <div className="chart-head">
        <div>
          <h3 id="history-heading">Observed price history</h3>
          <p>
            {filtered.length
              ? `${filtered[0].observedAt.slice(0, 10)} — ${filtered.at(-1)!.observedAt.slice(0, 10)} · ${filtered.length} actual observation${filtered.length === 1 ? '' : 's'}`
              : 'No observations in this range'}
          </p>
        </div>
        <div className="range" aria-label="History range">
          {(['1W', '1M', '1Y', '5Y'] as ChartRange[]).map(value => (
            <button
              type="button"
              className={range === value ? 'active' : ''}
              aria-pressed={range === value}
              onClick={() => setRange(value)}
              key={value}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {filtered.length < 2 || !geometry ? (
        <div className="no-chart">At least two actual observations are required to draw this range.</div>
      ) : (
        <svg viewBox="0 0 600 228" role="img" aria-label={`Observed USD market prices across ${range}`}>
          <line x1="62" y1="188" x2="580" y2="188" />
          <line x1="62" y1="20" x2="62" y2="188" />
          <text x="56" y="25" textAnchor="end">{formatUsd(geometry.maxPrice)}</text>
          <text x="56" y="191" textAnchor="end">{formatUsd(geometry.minPrice)}</text>
          <text x="62" y="212" textAnchor="start">{new Date(geometry.minTime).toISOString().slice(0, 10)}</text>
          <text x="580" y="212" textAnchor="end">{new Date(geometry.maxTime).toISOString().slice(0, 10)}</text>
          <path d={geometry.path} />
          {geometry.positioned.map(point => (
            <circle key={`${point.observedAt}-${point.price}`} cx={point.x} cy={point.y} r="3">
              <title>{point.observedAt.slice(0, 10)} · {formatUsd(point.price)} USD</title>
            </circle>
          ))}
        </svg>
      )}
      <p className="coverage-note">
        {sparse
          ? 'Sparse coverage: the line connects actual observations across gaps; no prices are interpolated.'
          : 'Daily provider snapshots are observations, not real-time ticks; no points are fabricated.'}
      </p>
    </section>
  );
}
